import { AZUR_TOKEN, REDIRECT_TYPE, REDIRECT_URL } from "./constant";

export const setRedirectURL = (value: string) => {
  window.localStorage.setItem(REDIRECT_URL, value);
};

export const setRedirectType = (value: string) => {
  window.localStorage.setItem(REDIRECT_TYPE, value);
};

export const clearRedirectSession = () => {
  window.localStorage.removeItem(REDIRECT_URL);
  window.localStorage.removeItem(REDIRECT_TYPE);
};

export const getRedirectData = () => {
  const url = window.localStorage.getItem(REDIRECT_URL);
  const type = window.localStorage.getItem(REDIRECT_TYPE);
  return { url, type };
};

export const setAzurToken = (value: string) => {
  window.localStorage.setItem(AZUR_TOKEN, JSON.stringify(value));
};

export const removeAzurToken = () => {
  window.localStorage.removeItem(AZUR_TOKEN);
};

export const getAzurToken = () => {
  const value: any = window.localStorage.getItem(AZUR_TOKEN);
  if (value) {
    const finalToken = value.substring(1, value.length - 1);
    return finalToken;
  } else {
    return null;
  }
};

export const validateEmailId = (email: string) => {
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  if (!emailRegex.test(email)) {
    return false;
  } else {
    return true;
  }
};

/**
 * Decode JWT token and get expiration information
 * @param token - JWT token string
 * @returns object with expiration details or null if invalid
 */
export const getTokenExpirationInfo = (
  token: string | null
): {
  isExpired: boolean;
  expirationTime: number | null;
  remainingSeconds: number | null;
  remainingMinutes: number | null;
  remainingHours: number | null;
  expirationDate: Date | null;
} | null => {
  if (!token) {
    return {
      isExpired: true,
      expirationTime: null,
      remainingSeconds: null,
      remainingMinutes: null,
      remainingHours: null,
      expirationDate: null,
    };
  }

  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
      return {
        isExpired: true,
        expirationTime: null,
        remainingSeconds: null,
        remainingMinutes: null,
        remainingHours: null,
        expirationDate: null,
      };
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    const decoded = JSON.parse(jsonPayload);
    const exp = decoded.exp;

    if (!exp) {
      return {
        isExpired: true,
        expirationTime: null,
        remainingSeconds: null,
        remainingMinutes: null,
        remainingHours: null,
        expirationDate: null,
      };
    }

    // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = exp;
    const remainingSeconds = expirationTime - currentTime;
    const isExpired = remainingSeconds <= 0;

    return {
      isExpired,
      expirationTime,
      remainingSeconds: isExpired ? 0 : remainingSeconds,
      remainingMinutes: isExpired ? 0 : Math.floor(remainingSeconds / 60),
      remainingHours: isExpired ? 0 : Math.floor(remainingSeconds / 3600),
      expirationDate: new Date(expirationTime * 1000),
    };
  } catch (error) {
    return {
      isExpired: true,
      expirationTime: null,
      remainingSeconds: null,
      remainingMinutes: null,
      remainingHours: null,
      expirationDate: null,
    };
  }
};

/**
 * Decode JWT token and check if it's expired
 * @param token - JWT token string
 * @returns boolean - true if token is expired or invalid, false if valid
 */
export const isTokenExpired = (token: string | null): boolean => {
  const info = getTokenExpirationInfo(token);
  return info?.isExpired ?? true;
};

/**
 * Check if the current session is expired and log remaining time
 * @param logToConsole - whether to log expiration info to console (default: true)
 * @returns boolean - true if session is expired, false if valid
 */
export const isSessionExpired = (logToConsole: boolean = true): boolean => {
  const token = getAzurToken();
  const info = getTokenExpirationInfo(token);

  if (logToConsole && info) {
    if (info.isExpired) {
    } else {
      const hours = info.remainingHours || 0;
      const minutes = (info.remainingMinutes || 0) % 60;
      const seconds = (info.remainingSeconds || 0) % 60;

      console.log("🟢 Session Status:", {
        "Time Remaining": `${hours}h ${minutes}m ${seconds}s`,
        "Total Seconds": info.remainingSeconds,
        "Expires At": info.expirationDate?.toLocaleString() || "N/A",
      });
    }
  }

  return info?.isExpired ?? true;
};
